import React, { useMemo, useState } from "react";
import type {
  BatchDeleteResult,
  DeleteProgress,
  Document,
  FailedDeletion,
} from "../types";
import { EyeIcon, TrashIcon } from "@heroicons/react/20/solid";
import { Button } from "../../../ui/Button";
import { DeleteDocumentDialog } from "./DeleteDocumentDialog";

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function statusBadgeVariant(status: string): string {
  switch (status) {
    case "processed":
      return "border-success/30 bg-success/15 text-success";
    case "pending":
      return "border-warning/30 bg-warning/15 text-warning-foreground";
    case "failed":
      return "border-destructive/30 bg-destructive/15 text-destructive";
    default:
      return "border-border bg-muted text-muted-foreground";
  }
}

interface DocumentsListProps {
  documents: Document[];
  /** How far a delete in flight has got, or null when none is. */
  deleteProgress?: DeleteProgress | null;
  /** Deletes the given documents, reporting which ones the API refused. */
  onDelete?: (documents: Document[]) => Promise<BatchDeleteResult>;
}

export function DocumentsList({
  documents,
  deleteProgress = null,
  onDelete,
}: DocumentsListProps) {
  const [viewingDoc, setViewingDoc] = useState<Document | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirmingDelete, setConfirmingDelete] = useState<Document[] | null>(
    null
  );
  const [failures, setFailures] = useState<FailedDeletion[]>([]);

  const isDeleting = deleteProgress !== null;
  // Derived rather than stored, so an id left over from a document that has
  // since gone can never be counted or deleted.
  const selectedDocs = useMemo(
    () => documents.filter((d) => selectedIds.has(d.id)),
    [documents, selectedIds]
  );
  const allSelected =
    documents.length > 0 && selectedDocs.length === documents.length;

  const toggleSelected = (documentId: string) => {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (!next.delete(documentId)) {
        next.add(documentId);
      }
      return next;
    });
  };

  const openDialog = (docs: Document[]) => {
    setFailures([]);
    setConfirmingDelete(docs);
  };

  const confirmDelete = async () => {
    if (!confirmingDelete || !onDelete) return;
    // Last attempt's refusals are stale the moment this one starts.
    setFailures([]);
    // Dismissing the dialog does not stop a fan-out already in flight, so a
    // refusal still has to reach someone: the dialog comes back carrying just
    // the documents that survived, and the reason each one gave.
    const result = await onDelete(confirmingDelete);
    setFailures(result.failed);
    // Whatever was deleted is gone from the refetched list, so it leaves the
    // selection too; a refusal keeps its row ticked so it can be retried.
    setSelectedIds((previous) => {
      const next = new Set(previous);
      result.deleted.forEach((id) => next.delete(id));
      return next;
    });
    if (result.failed.length === 0) {
      setConfirmingDelete(null);
      return;
    }
    const refused = new Set(result.failed.map((f) => f.documentId));
    setConfirmingDelete(confirmingDelete.filter((d) => refused.has(d.id)));
  };

  if (documents.length === 0) {
    return (
      <p className="text-muted-foreground text-sm py-4">
        No documents uploaded yet. Upload a document to get started.
      </p>
    );
  }

  return (
    <>
      {onDelete && selectedDocs.length > 0 && (
        <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted px-3 py-2 mb-4">
          <span className="text-sm text-foreground">
            {selectedDocs.length} selected
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedIds(new Set())}
              disabled={isDeleting}
            >
              Clear
            </Button>
            <Button
              variant="dangerOutline"
              size="sm"
              onClick={() => openDialog(selectedDocs)}
              disabled={isDeleting}
            >
              Delete selected
            </Button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              {onDelete && (
                <th className="px-4 py-2 w-8">
                  <input
                    type="checkbox"
                    className="rounded accent-primary"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) {
                        el.indeterminate = !allSelected && selectedDocs.length > 0;
                      }
                    }}
                    onChange={() =>
                      setSelectedIds(
                        allSelected
                          ? new Set()
                          : new Set(documents.map((d) => d.id))
                      )
                    }
                    disabled={isDeleting}
                    aria-label="Select all documents"
                  />
                </th>
              )}
              <th className="px-4 py-2 font-medium">Filename</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2 font-medium">Uploaded</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {documents.map((doc) => (
              <tr key={doc.id} className="border-b border-border">
                {onDelete && (
                  <td className="px-4 py-2">
                    <input
                      type="checkbox"
                      className="rounded accent-primary"
                      checked={selectedIds.has(doc.id)}
                      onChange={() => toggleSelected(doc.id)}
                      disabled={isDeleting}
                      aria-label={`Select ${doc.filename}`}
                    />
                  </td>
                )}
                <td className="px-4 py-2 font-medium max-w-xs truncate" title={doc.filename}>{doc.filename}</td>
                <td className="px-4 py-2">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${statusBadgeVariant(doc.status)}`}>
                        {doc.status}
                      </span>
                      {doc.error_message && (
                        <span
                          className="text-sm text-destructive"
                          title={doc.error_message}
                        >
                          ⚠
                        </span>
                      )}
                    </div>
                    {doc.status === "pending" &&
                      doc.document_type === "UPLOADED_EVALUATION" && (
                        <span className="text-xs text-muted-foreground">
                          You will be notified when ready (~24h)
                        </span>
                      )}
                    {doc.status === "failed" && doc.error_message && (
                      <span
                        className="text-xs text-destructive break-words max-w-md"
                        title={doc.error_message}
                      >
                        {doc.error_message}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-2 text-sm text-muted-foreground">
                  {formatDate(doc.created_at)}
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      type="button"
                      className="inline-flex items-center justify-center rounded-md h-8 w-8 p-0 text-foreground hover:bg-accent disabled:opacity-50"
                      onClick={() => setViewingDoc(doc)}
                      title="View details"
                    >
                      <EyeIcon className="w-4 h-4" />
                    </button>
                    {onDelete && (
                      <button
                        type="button"
                        className="inline-flex items-center justify-center rounded-md h-8 w-8 p-0 text-muted-foreground hover:bg-accent hover:text-destructive disabled:opacity-50"
                        onClick={() => openDialog([doc])}
                        disabled={isDeleting}
                        title="Delete document"
                        aria-label={`Delete ${doc.filename}`}
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {confirmingDelete && onDelete && (
        <DeleteDocumentDialog
          documents={confirmingDelete}
          progress={deleteProgress}
          failures={failures}
          onCancel={() => setConfirmingDelete(null)}
          onConfirm={confirmDelete}
        />
      )}

      {viewingDoc && (
        <dialog open className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-lg">
            <h3 className="font-bold text-lg text-foreground">Document details</h3>
            <div className="py-4 space-y-2">
              <p>
                <span className="font-medium">Filename:</span> {viewingDoc.filename}
              </p>
              <p>
                <span className="font-medium">Status:</span>{" "}
                <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${statusBadgeVariant(viewingDoc.status)}`}>
                  {viewingDoc.status}
                </span>
              </p>
              <p>
                <span className="font-medium">Uploaded:</span>{" "}
                {formatDate(viewingDoc.created_at)}
              </p>
              {viewingDoc.error_message && (
                <p>
                  <span className="font-medium">Error:</span>{" "}
                  <span className="text-destructive">{viewingDoc.error_message}</span>
                </p>
              )}
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-input bg-card px-4 h-9 text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50"
                onClick={() => setViewingDoc(null)}
              >
                Close
              </button>
            </div>
          </div>
          <form method="dialog" className="absolute inset-0 -z-10">
            <button type="submit" onClick={() => setViewingDoc(null)}>
              close
            </button>
          </form>
        </dialog>
      )}
    </>
  );
}
